// ═══════════════════════════════════════════════════════════════
// PROVISIONES — Tab Resumen General
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { useProvisionsSummary } from '../../../hooks/useProvisions';
import { provisionsAPI } from '../../../api/provisions';

const fmt = (n) =>
  new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const CODIGO_COLORS = {
  REGAL: { bg: 'bg-amber-50',  border: 'border-amber-200', text: 'text-amber-700',  dot: 'bg-amber-500'  },
  VAC:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',   dot: 'bg-blue-500'   },
  CES:   { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',    dot: 'bg-red-500'    },
  PREV:  { bg: 'bg-purple-50', border: 'border-purple-200',text: 'text-purple-700', dot: 'bg-purple-500' },
};

export default function ProvisionsDashboard({ companyId }) {
  const { data, loading, error, reload } = useProvisionsSummary(companyId);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcMsg, setRecalcMsg]         = useState('');

  const handleRecalcAll = async () => {
    if (!window.confirm('¿Recalcular provisiones para TODAS las nóminas históricas? Esto borrará y recalculará todos los acumulados.')) return;
    setRecalcLoading(true);
    setRecalcMsg('');
    try {
      const r = await provisionsAPI.recalculateAll(companyId);
      setRecalcMsg(`${r.mensaje}`);
      reload();
    } catch {
      setRecalcMsg('Error al recalcular. Revisa la consola del servidor.');
    } finally {
      setRecalcLoading(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error)   return <ErrorState message={error} onRetry={reload} />;
  if (!data)   return null;

  const { totalesPorTipo = [], topEmpleados = [], evolucion = [] } = data;

  const totalPasivo = totalesPorTipo.reduce((s, t) => s + parseFloat(t.saldo_total || 0), 0);
  const totalProvisionado = totalesPorTipo.reduce((s, t) => s + parseFloat(t.total_provisionado || 0), 0);
  const totalLiquidado = totalesPorTipo.reduce((s, t) => s + parseFloat(t.total_liquidado || 0), 0);

  return (
    <div className="space-y-6">
      {/* Botón recálculo histórico */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          {recalcMsg && (
            <p className={`text-sm font-medium ${recalcMsg.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
              {recalcMsg}
            </p>
          )}
        </div>
        <button
          onClick={handleRecalcAll}
          disabled={recalcLoading}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
        >
          {recalcLoading ? (
            <><span className="animate-spin">↻</span> Calculando...</>
          ) : (
            <>↻ Recalcular Histórico</>
          )}
        </button>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label="Total Provisionado"
          value={fmt(totalProvisionado)}
          sub="Acumulado histórico"
          color="blue"
        />
        <KpiCard
          label="Pasivo Vigente"
          value={fmt(totalPasivo)}
          sub="Pendiente de liquidar"
          color="amber"
        />
        <KpiCard
          label="Total Liquidado"
          value={fmt(totalLiquidado)}
          sub="Prestaciones pagadas"
          color="green"
        />
      </div>

      {/* Desglose por tipo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Pasivo por Tipo de Provisión</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {totalesPorTipo.map(t => {
            const c = CODIGO_COLORS[t.codigo] || CODIGO_COLORS.PREV;
            return (
              <div
                key={t.codigo}
                className={`rounded-lg border p-4 ${c.bg} ${c.border}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                  <span className={`text-xs font-bold uppercase tracking-wide ${c.text}`}>
                    {t.codigo}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">{t.nombre}</p>
                <p className={`text-xl font-bold ${c.text}`}>
                  RD$ {fmt(t.saldo_total)}
                </p>
                <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                  <div className="flex justify-between">
                    <span>Empleados:</span>
                    <span className="font-medium">{t.total_empleados}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Provisionado:</span>
                    <span className="font-medium">RD$ {fmt(t.total_provisionado)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Liquidado:</span>
                    <span className="font-medium">RD$ {fmt(t.total_liquidado)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top empleados */}
      {topEmpleados.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">
            Top Empleados — Mayor Pasivo Acumulado
          </h2>
          <div className="space-y-2">
            {topEmpleados.map((emp, idx) => {
              const pct = totalPasivo > 0
                ? Math.round((parseFloat(emp.pasivo_total) / totalPasivo) * 100)
                : 0;
              return (
                <div key={emp.id} className="flex items-center gap-3">
                  <span className="w-6 text-xs text-gray-400 text-right font-medium">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {emp.empleado}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 ml-2 shrink-0">
                        RD$ {fmt(emp.pasivo_total)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-amber-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Evolución reciente */}
      {evolucion.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">
            Evolución por Período (últimos períodos)
          </h2>
          <div className="overflow-x-auto">
            <EvolucionTable evolucion={evolucion} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tabla de evolución ─────────────────────────────────────────
function EvolucionTable({ evolucion }) {
  // Agrupar por periodo
  const periodos = [...new Set(evolucion.map(e => e.periodo))].sort().reverse().slice(0, 12);
  const codigos  = [...new Set(evolucion.map(e => e.codigo))];

  const map = {};
  evolucion.forEach(e => {
    if (!map[e.periodo]) map[e.periodo] = {};
    map[e.periodo][e.codigo] = parseFloat(e.total_periodo || 0);
  });

  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-2 pr-4 font-semibold text-gray-700">Período</th>
          {codigos.map(c => (
            <th key={c} className="text-right py-2 px-3 font-semibold text-gray-700">{c}</th>
          ))}
          <th className="text-right py-2 pl-3 font-semibold text-gray-700">Total</th>
        </tr>
      </thead>
      <tbody>
        {periodos.map(p => {
          const total = codigos.reduce((s, c) => s + (map[p]?.[c] || 0), 0);
          return (
            <tr key={p} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
              <td className="py-2 pr-4 font-medium text-gray-700">{p}</td>
              {codigos.map(c => (
                <td key={c} className="py-2 px-3 text-right text-gray-600">
                  {map[p]?.[c] ? fmt(map[p][c]) : '—'}
                </td>
              ))}
              <td className="py-2 pl-3 text-right font-semibold text-gray-800">
                {fmt(total)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Helpers de UI ──────────────────────────────────────────────
function KpiCard({ label, value, sub, color }) {
  const colors = {
    blue:  'from-blue-500 to-blue-600',
    amber: 'from-amber-500 to-amber-600',
    green: 'from-emerald-500 to-emerald-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-5 text-white`}>
      <p className="text-sm opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">RD$ {value}</p>
      <p className="text-xs opacity-70 mt-1">{sub}</p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500" />
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-medium">{message}</p>
      <button
        onClick={onRetry}
        className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
      >
        Reintentar
      </button>
    </div>
  );
}
